/**
 * Live plain-English translation of a compare-mode condition. The default is
 * the DIRECT comparison — "Matches when EPC is below CR over Last 30 days" —
 * because the % is opt-in: only when the user activates it does the copy
 * switch to tolerance language ("dropped more than 40% below…"). The worked
 * example prefers the campaign's REAL numbers (a live preview item) and
 * falls back to a 100-sample so the shown number needs no arithmetic.
 *
 * Copy per the three-angle writing panel merged by a judge (see PR). Special
 * cases: same metric + inherited window compares a number WITH ITSELF, so it
 * warns instead of pretending to be a trend check; `=`/`!=` are honest about
 * exact ties on live numbers; an invalid percent prompts instead.
 * For Media Devoted | Created By Ciro Autiero
 */

import { compareExample, type CompareSample } from './compare-example';
import {
  DIFF_METRIC,
  SAME_METRIC,
  equalitySummary,
  plainSummary,
  type CompareOp,
  type CompareTokens,
  type OrderedOp,
  type PercentBand,
} from './compare-sentence.tables';

export type { CompareOp } from './compare-sentence.tables';

/** The tag-rule operator spelling → canonical. */
export const TAG_OP_TO_CANONICAL: Record<string, CompareOp> = {
  gte: '>=',
  lte: '<=',
  gt: '>',
  lt: '<',
  eq: '=',
};

export interface CompareSentenceInput {
  /** Friendly metric names ("Unique EPC"), never raw ids. */
  lhsLabel: string;
  op: CompareOp;
  /** As stored: absent = the direct comparison, no % active. */
  percent?: number;
  rhsLabel: string;
  /** Named window label ("Last 30 days"); undefined = inherited. */
  windowLabel?: string;
  /** What "inherited" is called on this surface ("the rule's own window"). */
  inheritLabel: string;
  /** Real numbers from the live preview, when the surface has them. */
  sample?: CompareSample | null;
}

export type CompareHelp =
  | { kind: 'sentence'; summary: string; example: string }
  | { kind: 'warning'; text: string }
  | { kind: 'prompt'; text: string };

const trim = (n: number): string =>
  (Math.round(n * 100) / 100).toLocaleString('en-US');

function bandOf(p: number): PercentBand {
  if (p < 100) return 'low';
  if (p === 100) return 'even';
  return p < 200 ? 'high' : 'multi';
}

function tokensFor(input: CompareSentenceInput, p: number): CompareTokens {
  const window = input.windowLabel ?? input.inheritLabel;
  return {
    lhs: input.lhsLabel,
    R: `${input.rhsLabel} over ${window}`,
    windowLevel: `its ${window} level`,
    p: trim(p),
    delta: trim(Math.abs(100 - p)),
    x: trim(p / 100),
  };
}

function summaryFor(
  input: CompareSentenceInput,
  p: number | undefined,
  t: CompareTokens,
): string {
  const same = input.lhsLabel === input.rhsLabel;
  const ref = same ? t.windowLevel : t.R;
  if (input.op === '=' || input.op === '!=') {
    return equalitySummary(input.op, p === undefined ? 'even' : bandOf(p), t, ref);
  }
  if (p === undefined) return plainSummary(input.op as OrderedOp, t, ref);
  const table = same ? SAME_METRIC : DIFF_METRIC;
  return table[input.op as OrderedOp][bandOf(p)](t);
}

export function compareHelp(input: CompareSentenceInput): CompareHelp {
  const p = input.percent;
  if (p !== undefined && (!Number.isFinite(p) || p <= 0)) {
    return {
      kind: 'prompt',
      text: `Enter a percent above 0 — e.g. 80 means 80% of ${input.rhsLabel}.`,
    };
  }
  // Same metric over the SAME (inherited) period compares a number with
  // itself — never a trend check, whatever the percent.
  if (input.lhsLabel === input.rhsLabel && !input.windowLabel) {
    return {
      kind: 'warning',
      text: `This compares ${input.lhsLabel} with itself over the same period, so it can't work as a trend check — pick a fixed window like Last 30 days, or a different metric.`,
    };
  }
  return {
    kind: 'sentence',
    summary: summaryFor(input, p, tokensFor(input, p ?? 100)),
    example: compareExample({
      lhsLabel: input.lhsLabel,
      op: input.op,
      percent: p,
      rhsLabel: input.rhsLabel,
      window: input.windowLabel ?? input.inheritLabel,
      sample: input.sample,
    }),
  };
}
